import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import Layout from '../component/Layout';
import { Save, X, Plus, ChevronDown, ChevronUp, Search } from 'lucide-react';
import statesData from './StatenDistrict.json';
import './AreaDefault.css';

const AreaDefault = () => {
  const [states, setStates] = useState([]);
  const [operators, setOperators] = useState([]);
  const [areaDefaults, setAreaDefaults] = useState({});
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [expandedStates, setExpandedStates] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [searchText, setSearchText] = useState('');

  // Modal state
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');

  const segmentOptions = ['gold', 'silver', 'platinum', 'diamond', 'GEMS', 'PEARL', 'BANDHEL', 'DECOR', 'BRAND', 'TOOL', 'MACHINE'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setStates(statesData.states);

        const operatorSnap = await getDocs(collection(db, 'operatorregistrations'));
        const basicDetailsMap = {};
        operatorSnap.docs.forEach(doc => {
          basicDetailsMap[doc.id] = {
            name: doc.data().organizationName || doc.id,
            mobile: doc.data().mobile || doc.data().transactionalMobile || ''
          };
        });

        const segmentSnap = await getDocs(collection(db, 'OperatorSegmentRegistrations'));
        const operatorList = segmentSnap.docs.map(doc => {
          const data = doc.data();
          const basicDetails = basicDetailsMap[doc.id] || {};
          const zoneDistricts = Object.values(data.zone || {}).flat();

          return {
            id: doc.id,
            name: basicDetails.name,
            mobile: basicDetails.mobile,
            segment: data.segment || '',
            zone: data.zone || {},
            zoneDistricts: zoneDistricts
          };
        });
        setOperators(operatorList);

        const areaDefaultSnap = await getDocs(collection(db, 'AreaDefaultOperators'));
        const areaDefaultMap = {};
        areaDefaultSnap.docs.forEach(doc => {
          areaDefaultMap[doc.id] = doc.data();
        });
        setAreaDefaults(areaDefaultMap);

        console.log('[AreaDefault] Loaded operators:', operatorList);
        console.log('[AreaDefault] Loaded area defaults:', areaDefaultMap);
      } catch (err) {
        console.error('Error fetching data:', err);
        setMessage('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get segment name
  const getSegmentName = (segmentObj) => {
    if (typeof segmentObj === 'string') return segmentObj;
    return Object.keys(segmentObj || {})[0] || '';
  };

  // Get ALL operators that service this district and segment
  const getOperatorsForDistrictSegment = (district, segment) => {
    return operators.filter((op) => {
      const opSegment = getSegmentName(op.segment);
      const hasDistrict = op.zoneDistricts.includes(district);
      const hasSegment = opSegment === segment;
      return hasDistrict && hasSegment;
    });
  };

  // Get current assignment for district-segment
  const getCurrentAssignment = (district, segment) => {
    const key = `${district}_${segment}`;
    return areaDefaults[key];
  };

  // Calculate KPIs
  const calculateKPIs = () => {
    const allDistricts = states.flatMap(s => s.districts);
    const totalDistricts = allDistricts.length;
    
    const assignedDistricts = new Set();
    const totalAssignments = Object.keys(areaDefaults).length;
    
    Object.keys(areaDefaults).forEach(key => {
      const district = key.split('_')[0];
      assignedDistricts.add(district);
    });

    const coverage = totalDistricts > 0 
      ? Math.round((assignedDistricts.size / totalDistricts) * 100) 
      : 0;

    const activeOperators = new Set(Object.values(areaDefaults).map(a => a.operatorId)).size;

    return {
      totalDistricts,
      totalAssignments,
      activeOperators,
      coverage
    };
  };

  // Filter states and districts by search
  const getFilteredStates = () => {
    if (!searchText.trim()) return states;

    return states
      .map(stateObj => ({
        ...stateObj,
        districts: stateObj.districts.filter(dist =>
          dist.toLowerCase().includes(searchText.toLowerCase())
        )
      }))
      .filter(stateObj => stateObj.districts.length > 0);
  };

  // Open modal to assign operator
  const openAssignModal = (district) => {
    setSelectedDistrict(district);
    setSelectedSegment('');
    setSelectedOperator('');
    setMessage('');
    setShowModal(true);
  };

  // Save operator assignment
  const handleSaveAssignment = async () => {
    if (!selectedDistrict || !selectedSegment || !selectedOperator) {
      setMessage('❌ Select segment and operator');
      return;
    }

    setSaving(true);
    try {
      const operator = operators.find(op => op.id === selectedOperator);
      const docId = `${selectedDistrict}_${selectedSegment}`;

      await setDoc(doc(db, 'AreaDefaultOperators', docId), {
        district: selectedDistrict,
        segment: selectedSegment,
        operatorId: selectedOperator,
        operatorName: operator.name,
        mobile: operator.mobile,
        assignedAt: new Date(),
        updatedAt: new Date()
      }, { merge: true });

      setAreaDefaults(prev => ({
        ...prev,
        [docId]: {
          district: selectedDistrict,
          segment: selectedSegment,
          operatorId: selectedOperator,
          operatorName: operator.name,
          mobile: operator.mobile,
          assignedAt: new Date()
        }
      }));

      console.log(`✅ [Area Default] ${selectedDistrict} - ${selectedSegment.toUpperCase()}: ${selectedOperator}`);
      setMessage(`✅ Operator assigned for ${selectedSegment.toUpperCase()} in ${selectedDistrict}`);
      setTimeout(() => {
        setShowModal(false);
        setSelectedDistrict(null);
      }, 1500);
    } catch (err) {
      console.error('Error saving:', err);
      setMessage('❌ Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const toggleState = (state) => {
    setExpandedStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDistrict(null);
    setMessage('');
  };

  if (loading) {
    return (
      <Layout pageTitle="Area Default Operators">
        <div className="area-default">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  const kpis = calculateKPIs();
  const filteredStates = getFilteredStates();

  return (
    <Layout pageTitle="Area Default Operators">
      <div className="area-default">
        <div className="page-header">
          <div>
            <h2>Area Default Operators</h2>
            <p className="subtitle">Assign default operators by district and segment</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-value">{kpis.totalDistricts}</div>
            <div className="kpi-label">Total Districts</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value">{kpis.totalAssignments}</div>
            <div className="kpi-label">Total Assignments</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value">{kpis.activeOperators}</div>
            <div className="kpi-label">Active Operators</div>
          </div>
          <div className="kpi-card highlight">
            <div className="kpi-value">{kpis.coverage}%</div>
            <div className="kpi-label">Coverage</div>
          </div>
        </div>

        {/* Search Box */}
        <div className="search-section">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Search districts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
            />
            {searchText && (
              <button
                className="search-clear"
                onClick={() => setSearchText('')}
              >
                ✕
              </button>
            )}
          </div>
          {searchText && (
            <p className="search-results-info">
              Found {filteredStates.reduce((sum, s) => sum + s.districts.length, 0)} districts
            </p>
          )}
        </div>

        {/* States & Districts */}
        <div className="states-container">
          {filteredStates.length > 0 ? (
            filteredStates.map(stateObj => {
              const isExpanded = expandedStates[stateObj.state];
              const assignedCount = stateObj.districts.filter(dist => {
                return segmentOptions.some(seg => 
                  getCurrentAssignment(dist, seg)
                );
              }).length;

              return (
                <div key={stateObj.state} className="state-section">
                  <button
                    className="state-header"
                    onClick={() => toggleState(stateObj.state)}
                  >
                    <div className="state-info">
                      <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </span>
                      <h3>{stateObj.state}</h3>
                      <span className="district-count">
                        {assignedCount}/{stateObj.districts.length} assigned
                      </span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="districts-list">
                      {stateObj.districts.map(district => {
                        const hasAssignments = segmentOptions.some(seg => getCurrentAssignment(district, seg));
                        const assignmentCount = segmentOptions.filter(seg => getCurrentAssignment(district, seg)).length;

                        return (
                          <div key={district} className="district-item">
                            <div className="district-header">
                              <div className="district-name">
                                <span className="name">{district}</span>
                                {hasAssignments && (
                                  <span className="assigned-badge">{assignmentCount} assignments</span>
                                )}
                              </div>
                              <button
                                className="btn-assign-segment"
                                onClick={() => openAssignModal(district)}
                              >
                                <Plus size={16} /> Assign Operator
                              </button>
                            </div>

                            <div className="assignments-grid">
                              {segmentOptions.map(segment => {
                                const assignment = getCurrentAssignment(district, segment);
                                return (
                                  <div key={segment} className={`assignment-card ${assignment ? 'assigned' : 'empty'}`}>
                                    <div className="segment-badge">{segment.toUpperCase()}</div>
                                    {assignment ? (
                                      <div className="assignment-info">
                                        <div className="operator-name">{assignment.operatorName}</div>
                                        <div className="operator-id">{assignment.operatorId}</div>
                                      </div>
                                    ) : (
                                      <div className="no-operator">Unassigned</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="no-results">
              <p>No districts found matching "{searchText}"</p>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && selectedDistrict && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h3>Assign Operator</h3>
                  <p className="modal-subtitle">District: <strong>{selectedDistrict}</strong></p>
                </div>
                <button className="btn-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>

              {message && (
                <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <div className="modal-body">
                <div className="form-group">
                  <label>Select Segment</label>
                  <select
                    value={selectedSegment}
                    onChange={(e) => {
                      setSelectedSegment(e.target.value);
                      setSelectedOperator('');
                    }}
                    className="form-select"
                  >
                    <option value="">Choose Segment</option>
                    {segmentOptions.map(seg => (
                      <option key={seg} value={seg}>{seg.toUpperCase()}</option>
                    ))}
                  </select>
                </div>

                {selectedSegment && (
                  <div className="form-group">
                    <label>
                      Available Operators 
                      <span className="operator-count">
                        {getOperatorsForDistrictSegment(selectedDistrict, selectedSegment).length} available
                      </span>
                    </label>
                    <div className="operators-list">
                      {getOperatorsForDistrictSegment(selectedDistrict, selectedSegment).length > 0 ? (
                        getOperatorsForDistrictSegment(selectedDistrict, selectedSegment).map((op, idx) => {
                          const isCurrent = selectedOperator === op.id;
                          const isAssigned = getCurrentAssignment(selectedDistrict, selectedSegment)?.operatorId === op.id;

                          return (
                            <button
                              key={`${op.id}-${idx}`}
                              className={`operator-option ${isCurrent ? 'selected' : ''} ${isAssigned ? 'currently-assigned' : ''}`}
                              onClick={() => setSelectedOperator(op.id)}
                            >
                              <div className="operator-name">{op.name}</div>
                              <div className="operator-details">
                                <span className="operator-id">{op.id}</span>
                                <span className="operator-mobile">{op.mobile}</span>
                              </div>
                              {isAssigned && <span className="current-label">Currently Assigned</span>}
                            </button>
                          );
                        })
                      ) : (
                        <p className="no-operators">
                          No operators available for {selectedSegment.toUpperCase()} in {selectedDistrict}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  className="btn-save-modal"
                  onClick={handleSaveAssignment}
                  disabled={saving || !selectedSegment || !selectedOperator}
                >
                  <Save size={16} /> {saving ? 'Saving...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AreaDefault;
